import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { TanStackTableRoot } from '@toggle/design-system';
import { isElementScrollbarVisible } from '@toggle/helpers';
import {
  EarningsItemWithEntity,
  EarningsPeriod,
  Entity,
  mapEntity,
} from '@toggle/toggle';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { parseEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { queryKeys, queryValues } from '~/routes/query-params';
import { getEarningsInsightData } from '~/services/insights/insights-service';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { EarningsDrawer } from '~/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import {
  EARNINGS_COLUMN_ID,
  getCellHighlighted,
  QNA_COLUMN_ID,
  RELEASED_ON_COLUMN_ID,
  SELECTED_COLUMN_ID,
} from '../../utils/utils';
import { EarningsTableHeaderCell } from '../EarningsTableHeaderCell';
import * as S from './EarningsTable.styles';

interface EarningsTableProps {
  columns: ColumnDef<EarningsPeriod>[];
  entity: Entity;
}

export const EarningsTable = ({ columns, entity }: EarningsTableProps) => {
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    activeSortingState,
    rowSelection,
    isViewSelectedOnly,
    earningsData,
    earningExchange,
    resetAllFilterOptions,
  } = useEarningsStore();
  const { t } = useTranslation();
  const { openDrawer } = useEarningsInsightDrawer();
  const [drawerData, setDrawerData] = useState<
    EarningsItemWithEntity | undefined
  >();
  const { deleteQueryParam, setQueryParams } = useQueryParams();

  useEffect(
    () => setHasHorizontalScroll(isElementScrollbarVisible(containerRef)),
    [containerRef]
  );

  const table = useReactTable({
    data: earningsData,
    columns,
    enableMultiSort: false,
    enableRowSelection: true,
    state: {
      sorting: activeSortingState,
      rowSelection,
      columnPinning: {
        left: [SELECTED_COLUMN_ID, EARNINGS_COLUMN_ID, QNA_COLUMN_ID],
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: data => data.reporting_date,
  });

  const tableRows = table.getRowModel().rows;
  const tableRowsToDisplay = isViewSelectedOnly
    ? tableRows.filter(row => row.getIsSelected())
    : tableRows;

  const isCellNumber = (columnId: string) => {
    return (
      columnId !== EARNINGS_COLUMN_ID && columnId !== RELEASED_ON_COLUMN_ID
    );
  };

  const getIsPinnedVertically = (column: Column<EarningsPeriod>) =>
    hasHorizontalScroll && !!column.getIsPinned();

  const onRowClick = async (row: Row<EarningsPeriod>) => {
    const earningsInsightsData = await getEarningsInsightData(row.original.id);
    const earningsInsight = parseEarningsInsights(
      earningsInsightsData.data ?? []
    );

    if (earningsInsight) {
      openDrawer({
        entityTag: entity.tag,
        earningsId: row.original.id,
        subtab:
          earningsInsight.type === 'preview'
            ? SubTabsType.Preview
            : SubTabsType.Recap,
        source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      });
    } else {
      setDrawerData({
        ...row.original,
        entity_tag: entity.tag,
        entity: mapEntity(entity),
        exchange: earningExchange,
      });
      setQueryParams(
        {
          [queryKeys.activeDrawer]: queryValues.earningsDrawer,
        },
        { replace: true }
      );
    }
  };

  const onDrawerClose = () => {
    deleteQueryParam([queryKeys.activeDrawer], { replace: true });
    setDrawerData(undefined);
  };

  if (!tableRowsToDisplay.length) {
    return (
      <S.ErrorMessageAndResetButtonWrapper>
        <S.StyledErrorMessageWrapper
          variant="empty"
          displayIcon
          title={t('widget:earnings.empty.title')}
          subTitle={t('widget:earnings.empty.emptyFilteredResultsSubTitle')}
        />
        <S.StyledButton
          data-testid="earnings-reset-all-filters-button"
          label={t('widget:earnings.resetAll')}
          onClick={resetAllFilterOptions}
          variant="empty"
          size="small"
          iconName="Reset"
        />
      </S.ErrorMessageAndResetButtonWrapper>
    );
  }

  return (
    <>
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
      <S.TableScrollContainer ref={containerRef}>
        <TanStackTableRoot data-testid="earnings-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup, index, arr) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isLastHeaderGroup = index === arr.length - 1;
                  return (
                    <EarningsTableHeaderCell
                      key={header.id}
                      header={header}
                      isLastHeaderGroup={isLastHeaderGroup}
                      isPinnedVertically={getIsPinnedVertically(header.column)}
                      isHighlighted={getCellHighlighted(header.column.id)}
                    />
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableRowsToDisplay.map(row => (
              <tr key={row.id} onClick={() => onRowClick(row)}>
                {row.getVisibleCells().map((cell, idx) => {
                  return (
                    <S.StyledTableCell
                      key={cell.id}
                      isNumber={isCellNumber(cell.column.id)}
                      alignment={idx > 2 ? 'end' : 'start'}
                      $isPinned={getIsPinnedVertically(cell.column)}
                      $left={cell.column.getStart('left')}
                      $right={cell.column.getAfter('right')}
                      $isLastPinnedCol={
                        getIsPinnedVertically(cell.column) &&
                        cell.column.getIsLastColumn('left')
                      }
                      $highlight={getCellHighlighted(cell.column.id)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </S.StyledTableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </TanStackTableRoot>
      </S.TableScrollContainer>
    </>
  );
};
